import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Save, CheckCircle2, Send, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STEPS = [
  <>Öffne <a className="text-blue-600 underline" href="https://entra.microsoft.com" target="_blank" rel="noreferrer">entra.microsoft.com <ExternalLink size={11} className="inline" /></a> und melde Dich mit Deinem Microsoft-365-Admin-Konto an.</>,
  <>Links im Menü: <b>Identität → Anwendungen → App-Registrierungen</b>. Oben auf <b>„+ Neue Registrierung"</b> klicken.</>,
  <>Name: <b>Club-Magazin Mailversand</b>. Kontotyp: <b>„Nur Konten in diesem Organisationsverzeichnis"</b>. Umleitungs-URI leer lassen → <b>Registrieren</b>.</>,
  <>Du siehst jetzt die <b>Übersicht</b>. Kopiere die <b>„Anwendungs-ID (Client)"</b> und die <b>„Verzeichnis-ID (Mandant)"</b> unten in die Felder.</>,
  <>Links auf <b>„API-Berechtigungen"</b> → <b>„+ Berechtigung hinzufügen"</b> → <b>Microsoft Graph</b> → <b>Anwendungsberechtigungen</b> → in der Suche <b>Mail.Send</b> eingeben, Haken setzen → <b>Berechtigungen hinzufügen</b>.</>,
  <>Zurück in der Liste: Button <b>„Administratorzustimmung für … erteilen"</b> klicken → <b>Ja</b>. Bei Mail.Send erscheint ein <b>grüner Haken</b>.</>,
  <>Links auf <b>„Zertifikate &amp; Geheimnisse"</b> → <b>„+ Neuer geheimer Clientschlüssel"</b> → Beschreibung „Redaktion", Ablauf <b>24 Monate</b> → Hinzufügen.</>,
  <>Kopiere <b>sofort</b> die Spalte <b>„Wert"</b> (nicht die „Geheime ID"!) – der Wert wird später nicht mehr angezeigt. Unten als Client-Geheimnis einfügen.</>,
  <>Absenderadresse eintragen, <b>Speichern</b> klicken und eine <b>Testmail</b> senden. Fertig!</>,
];

function Guide() {
  return (
    <div className="mt-6 bg-blue-50/60 border border-blue-100 rounded-xl p-4 text-sm text-slate-700" data-testid="ms365-guide">
      <p className="font-semibold text-slate-800 mb-2">Einrichtung in 9 Schritten (einmalig, ca. 10 Minuten):</p>
      <ol className="list-decimal ml-5 space-y-1.5">{STEPS.map((s, i) => <li key={i}>{s}</li>)}</ol>
      <p className="mt-3 text-xs text-slate-500">Hinweis: Das frühere „Authentifiziertes SMTP" mit Passwort wurde von Microsoft 2026 abgeschaltet. Dieser Weg ist der offizielle Nachfolger – ohne Passwort, Mails landen in Ihrem Outlook unter „Gesendete Elemente".</p>
    </div>
  );
}

export default function MailSettings() {
  const [cfg, setCfg] = useState({ tenant_id: "", client_id: "", sender: "", has_secret: false });
  const [secret, setSecret] = useState("");
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/ms365/config"); setCfg(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/ms365/config", { tenant_id: cfg.tenant_id, client_id: cfg.client_id, sender: cfg.sender, client_secret: secret });
      setCfg(data); setSecret("");
      toast.success("Zugangsdaten gespeichert");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/ms365/test", { to: testTo }); toast.success(`Testmail an ${data.to} gesendet`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const field = (key, label, ph) => (
    <div>
      <Label>{label}</Label>
      <Input value={cfg[key]} onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })} className="mt-1 mono" placeholder={ph} data-testid={`ms365-${key}`} />
    </div>
  );
  const ready = cfg.has_secret && cfg.tenant_id && cfg.client_id && cfg.sender;

  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-3 mb-1">
        <div className="h-11 w-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600"><Mail size={22} /></div>
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">E-Mail-Versand über Microsoft 365</h1>
          <p className="text-slate-500 text-sm">Mails gehen direkt aus Deinem Postfach raus – kein Spam, Antworten landen bei Dir.</p>
        </div>
      </div>
      <Guide />
      <div className="mt-6 bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-sm font-bold uppercase tracking-wider text-slate-600">Microsoft-Zugangsdaten</h2>
          {ready ? <span className="text-xs text-emerald-600 font-medium inline-flex items-center gap-1" data-testid="ms365-status"><CheckCircle2 size={14} /> Eingerichtet</span>
            : <span className="text-xs text-slate-400" data-testid="ms365-status">Noch nicht eingerichtet</span>}
        </div>
        <div className="space-y-4">
          {field("tenant_id", "Verzeichnis-ID (Mandant)", "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx")}
          {field("client_id", "Anwendungs-ID (Client)", "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx")}
          <div>
            <Label>Client-Geheimnis (Wert) {cfg.has_secret && <span className="text-emerald-600 font-normal">(hinterlegt – leer lassen, um nicht zu ändern)</span>}</Label>
            <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} className="mt-1 mono" placeholder={cfg.has_secret ? "••••••••" : "Wert des geheimen Clientschlüssels"} data-testid="ms365-client-secret" />
          </div>
          {field("sender", "Absender-Postfach", "c.gaertner@rot-weiss-berlin.org")}
          <Button onClick={save} disabled={busy} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="ms365-save"><Save size={16} className="mr-1.5" /> Speichern</Button>
        </div>
      </div>
      <div className="mt-6 bg-emerald-50/50 border border-emerald-100 rounded-xl p-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-wider text-slate-700 mb-3">Testmail senden</h2>
        <div className="flex gap-2 flex-wrap">
          <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Empfänger (leer = Deine Login-Adresse)" className="flex-1 min-w-[240px]" data-testid="ms365-test-to" />
          <Button onClick={test} disabled={busy || !ready || !user?.is_chef} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ms365-test-send"><Send size={15} className="mr-1.5" /> Testmail senden</Button>
        </div>
      </div>
    </div>
  );
}
